import { HashMap } from 'shared/types/commonView';
import { isObjectIdString, OBJECT_ID_LENGTH } from 'helpers/idComparison';

export const COMPLIANCE_ANSWER_OPTION_PREFIX = 'compliance-check-form-result';
export const COMPLIANCE_CHECK_FORM_PREFIX = 'compliance-check-form';
export const PROTOCOL_ITEM_COMMENT_PREFIX = 'protocol-item-comment';
export const LEGACY_PROTOCOL_ITEM_COMMENT_PREFIX = 'compliance-check-form-comment';
export const COMPLIANCE_CHECK_FORM_COMMENT = LEGACY_PROTOCOL_ITEM_COMMENT_PREFIX;
export const NUMERIC_ANSWER_PREFIX = 'protocol-numeric-answer';
export const TEXT_ANSWER_PREFIX = 'protocol-text-answer';
export const OPTION_SELECTION_ANSWER_PREFIX =
  'protocol-option-selection-answer';
export const PROTOCOL_SECTION_COMMENT_PREFIX = 'protocol-section-comment';

const PROTOCOL_FORM_PREFIXES_WITH_CHECKLIST_ITEM_ID = [
  PROTOCOL_ITEM_COMMENT_PREFIX,
  LEGACY_PROTOCOL_ITEM_COMMENT_PREFIX,
  COMPLIANCE_ANSWER_OPTION_PREFIX,
  NUMERIC_ANSWER_PREFIX,
  TEXT_ANSWER_PREFIX,
  OPTION_SELECTION_ANSWER_PREFIX,
] as const;

export function buildComplianceAnswerFormKey(
  checklistItemId: string,
  answerId: string
): string {
  return `${COMPLIANCE_ANSWER_OPTION_PREFIX}-${checklistItemId}-${answerId}`;
}

export function buildLegacyComplianceAnswerFormKey(checklistItemId: string): string {
  return `${COMPLIANCE_ANSWER_OPTION_PREFIX}-${checklistItemId}`;
}

export function buildNumericAnswerFormKey(
  checklistItemId: string,
  answerId: string
): string {
  return `${NUMERIC_ANSWER_PREFIX}-${checklistItemId}-${answerId}`;
}

export function buildTextAnswerFormKey(
  checklistItemId: string,
  answerId: string
): string {
  return `${TEXT_ANSWER_PREFIX}-${checklistItemId}-${answerId}`;
}

export function buildOptionSelectionAnswerFormKey(
  checklistItemId: string,
  answerId: string
): string {
  return `${OPTION_SELECTION_ANSWER_PREFIX}-${checklistItemId}-${answerId}`;
}

export function buildProtocolItemCommentFormKey(checklistItemId: string): string {
  return `${PROTOCOL_ITEM_COMMENT_PREFIX}-${checklistItemId}`;
}

export function buildProtocolSectionCommentFormKey(sectionId: string): string {
  return `${PROTOCOL_SECTION_COMMENT_PREFIX}-${sectionId}`;
}

export function buildLegacyProtocolItemCommentFormKey(
  checklistItemId: string
): string {
  return `${LEGACY_PROTOCOL_ITEM_COMMENT_PREFIX}-${checklistItemId}`;
}

export function readProtocolItemCommentFromForm(
  form: HashMap<unknown>,
  checklistItemId: string
): string {
  const canonical = form[buildProtocolItemCommentFormKey(checklistItemId)];
  if (typeof canonical === 'string') {
    return canonical;
  }

  const legacy = form[buildLegacyProtocolItemCommentFormKey(checklistItemId)];
  if (typeof legacy === 'string') {
    return legacy;
  }

  return '';
}

function checklistItemIdFromFormKeySuffix(suffix: string): string | undefined {
  if (isObjectIdString(suffix)) {
    return suffix;
  }

  if (
    suffix.length > OBJECT_ID_LENGTH + 1 &&
    suffix[OBJECT_ID_LENGTH] === '-' &&
    isObjectIdString(suffix.slice(0, OBJECT_ID_LENGTH))
  ) {
    return suffix.slice(0, OBJECT_ID_LENGTH);
  }

  const dashIndex = suffix.indexOf('-');
  if (dashIndex > 0) {
    return suffix.slice(0, dashIndex);
  }

  return suffix.length > 0 ? suffix : undefined;
}

export function extractChecklistItemIdsFromProtocolForm(
  form: HashMap<unknown>
): string[] {
  const ids = new Set<string>();

  Object.keys(form).forEach((key) => {
    PROTOCOL_FORM_PREFIXES_WITH_CHECKLIST_ITEM_ID.forEach((prefix) => {
      if (!key.startsWith(`${prefix}-`)) {
        return;
      }

      const suffix = key.slice(prefix.length + 1);
      const checklistItemId = checklistItemIdFromFormKeySuffix(suffix);
      if (checklistItemId) {
        ids.add(checklistItemId);
      }
    });
  });

  return [...ids];
}
